import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, ActivityIcon, HeartPulseIcon, StethoscopeIcon, ClipboardCheckIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Medical Services
{
  id: 'ms1',
  title: 'Emergency Medical Response',
  description: 'Request emergency medical services and ambulance dispatch',
  category: 'Medical Services',
  estimatedTime: 'Immediate',
  fee: 'Varies by service'
}, {
  id: 'ms2',
  title: 'Community Health Screening',
  description: 'Access free health screening and basic medical checks',
  category: 'Medical Services',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}, {
  id: 'ms3',
  title: 'Vaccination Services',
  description: 'Schedule routine or seasonal vaccinations',
  category: 'Medical Services',
  estimatedTime: '2-3 business days',
  fee: 'Varies by vaccine'
}, {
  id: 'ms4',
  title: 'Mobile Medical Unit',
  description: 'Request mobile medical services for community events',
  category: 'Medical Services',
  estimatedTime: '5-7 business days',
  fee: '$200.00 per day'
},
// Public Health Programs
{
  id: 'ph1',
  title: 'Disease Prevention Program',
  description: 'Join community disease prevention and education initiatives',
  category: 'Public Health Programs',
  estimatedTime: '3-5 business days',
  fee: 'No fee'
}, {
  id: 'ph2',
  title: 'Mental Health Support',
  description: 'Access mental health resources and counseling services',
  category: 'Public Health Programs',
  estimatedTime: '2-3 business days',
  fee: 'Sliding scale'
}, {
  id: 'ph3',
  title: 'Maternal Health Program',
  description: 'Register for maternal health support and education',
  category: 'Public Health Programs',
  estimatedTime: '3-5 business days',
  fee: 'No fee'
}, {
  id: 'ph4',
  title: 'Senior Wellness Program',
  description: 'Join health programs designed for senior citizens',
  category: 'Public Health Programs',
  estimatedTime: '3-5 business days',
  fee: 'No fee'
},
// Healthcare Facilities
{
  id: 'hf1',
  title: 'Clinic Registration',
  description: 'Register new healthcare facility or clinic',
  category: 'Healthcare Facilities',
  estimatedTime: '10-15 business days',
  fee: '$500.00'
}, {
  id: 'hf2',
  title: 'Facility Inspection',
  description: 'Schedule healthcare facility compliance inspection',
  category: 'Healthcare Facilities',
  estimatedTime: '5-7 business days',
  fee: '$300.00'
}, {
  id: 'hf3',
  title: 'Equipment Certification',
  description: 'Obtain certification for medical equipment',
  category: 'Healthcare Facilities',
  estimatedTime: '7-10 business days',
  fee: '$250.00'
}, {
  id: 'hf4',
  title: 'Laboratory Licensing',
  description: 'Apply for medical laboratory operation license',
  category: 'Healthcare Facilities',
  estimatedTime: '15-20 business days',
  fee: '$750.00'
},
// Health Permits & Licenses
{
  id: 'hp1',
  title: 'Medical Practice License',
  description: 'Apply for license to operate medical practice',
  category: 'Health Permits & Licenses',
  estimatedTime: '15-20 business days',
  fee: '$400.00'
}, {
  id: 'hp2',
  title: 'Pharmacy Permit',
  description: 'Obtain permit for pharmacy operations',
  category: 'Health Permits & Licenses',
  estimatedTime: '10-15 business days',
  fee: '$350.00'
}, {
  id: 'hp3',
  title: 'Health Event Permit',
  description: 'Request permit for public health events',
  category: 'Health Permits & Licenses',
  estimatedTime: '5-7 business days',
  fee: '$150.00'
}, {
  id: 'hp4',
  title: 'Medical Waste Disposal',
  description: 'Register for medical waste disposal services',
  category: 'Health Permits & Licenses',
  estimatedTime: '7-10 business days',
  fee: '$200.00'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Medical Services':
      return <HeartPulseIcon className="h-6 w-6 text-red-600" data-id="element-3764" />;
    case 'Public Health Programs':
      return <ActivityIcon className="h-6 w-6 text-blue-600" data-id="element-3765" />;
    case 'Healthcare Facilities':
      return <StethoscopeIcon className="h-6 w-6 text-green-600" data-id="element-3766" />;
    case 'Health Permits & Licenses':
      return <ClipboardCheckIcon className="h-6 w-6 text-purple-600" data-id="element-3767" />;
    default:
      return <ActivityIcon className="h-6 w-6 text-gray-600" data-id="element-3768" />;
  }
};
const ServiceCard = ({
  service
}: {
  service: Service;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-3769">
      <div className="p-5 flex flex-col h-full" data-id="element-3770">
        <div className="flex-1" data-id="element-3771">
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-3772">
            {service.title}
          </h3>
          <p className="text-sm text-gray-600 mb-6 line-clamp-2" data-id="element-3773">
            {service.description}
          </p>
        </div>
        <div className="space-y-4" data-id="element-3774">
          <div className="flex items-center space-x-4 text-sm text-gray-500 pt-4 border-t border-gray-100" data-id="element-3775">
            <div className="flex items-center" data-id="element-3776">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3777">
                ⏱
              </span>
              <span data-id="element-3778">{service.estimatedTime}</span>
            </div>
            <div className="flex items-center" data-id="element-3779">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-3780">
                💰
              </span>
              <span data-id="element-3781">{service.fee}</span>
            </div>
          </div>
          <button className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-3782">
            Access Service
            <span className="ml-1" data-id="element-3783">→</span>
          </button>
        </div>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  services
}: {
  category: string;
  services: Service[];
}) => {
  return <div className="mb-8" data-id="element-3784">
      <div className="flex items-center mb-4" data-id="element-3785">
        <CategoryIcon category={category} data-id="element-3786" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2" data-id="element-3787">{category}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3788">
        {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-3789" />)}
      </div>
    </div>;
};
export const HealthServicesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3790">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3791">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3792">
          <a href="/" className="hover:text-gray-700" data-id="element-3793">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3794" />
          <span className="text-gray-900" data-id="element-3795">Health Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3796">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3797">
            Health Services
          </h1>
          <p className="text-gray-600" data-id="element-3798">
            Access medical services, public health programs, and healthcare
            facility management
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3799">
          <div className="max-w-md relative" data-id="element-3800">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3801">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3802" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3803" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} data-id="element-3804" />)}
      </div>
    </div>;
};